import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Modal,
  Typography,
  styled,
  keyframes,
  IconButton,
  CircularProgress,
  Container,
  useTheme,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ProgressBar = styled(Box)(({ theme }) => ({
  height: 4,
  backgroundColor: theme.palette.primary.light,
  width: "100%",
  position: "relative",
  borderRadius: 2,
  overflow: "hidden",
  "&:after": {
    content: '""',
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    backgroundColor: theme.palette.primary.main,
    width: "100%",
    animation: `${keyframes`from { width: 100% } to { width: 0 }`} 10s linear forwards`,
  },
}));

const ModernModal = styled(Modal)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backdropFilter: "blur(3px)",
}));

const ModalContent = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: 16,
  padding: theme.spacing(4),
  maxWidth: 400,
  width: "90%",
  textAlign: "center",
  position: "relative",
  animation: `${fadeIn} 0.3s ease-out`,
  boxShadow: theme.shadows[10],
  border: `1px solid ${theme.palette.divider}`,
}));

const SuccessModal = () => {
  const [open, setOpen] = useState(false);
  const [timeoutId, setTimeoutId] = useState(null);
  const theme = useTheme();

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    clearTimeout(timeoutId);
  };

  useEffect(() => {
    if (open) {
      const id = setTimeout(handleClose, 10000);
      setTimeoutId(id);
      return () => clearTimeout(id);
    }
  }, [open]);

  return (
    <Container
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
      }}
    >
      <Box
        sx={{
          textAlign: "center",
          mb: 4,
        }}
      >
        <Typography variant="h2" component="h1" gutterBottom>
          Coming Soon
        </Typography>
        <Typography variant="h5" component="p">
          We are working hard to bring you something amazing. Stay tuned!
        </Typography>
        <Button variant="contained" sx={{ my: 2 }} onClick={handleOpen}>
          Notify Me!
        </Button>
      </Box>

      <ModernModal open={open} onClose={handleClose}>
        <ModalContent>
          <ProgressBar />

          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              color: "text.secondary",
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Box sx={{ mt: 3, mb: 4 }}>
            <CheckCircleOutlineIcon
              sx={{
                fontSize: 64,
                color: "success.main",
                animation: `${keyframes`
                  from { transform: scale(0); opacity: 0 }
                  to { transform: scale(1); opacity: 1 }
                `} 0.5s ease-out`,
              }}
            />
          </Box>

          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            Thank You! 🎉
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your submission has been received successfully. This modal will
            automatically close in 10 seconds.
          </Typography>

          <Button
            variant="contained"
            onClick={handleClose}
            fullWidth
            sx={{
              py: 1.5,
              borderRadius: 8,
              textTransform: "none",
              fontWeight: 600,
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: 3,
              },
            }}
          >
            Close Now
          </Button>
        </ModalContent>
      </ModernModal>
    </Container>
  );
};

export default SuccessModal;
